import { Component, Inject, OnInit, signal, computed } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { IPage } from '../../../services/base/page';
import { RoutePageComponent } from '../../shared/route-page/route-page.component';
import { CommonModule } from '@angular/common';
import { Field, FieldControlService, IField, ButtonComponent, InputTextComponent } from '@pacificspecialtyinsurancecompany/psic-ng-components';
import { globalConstants } from '../../shared/global-constants';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatChipsModule } from '@angular/material/chips';
import { BreakpointObserver } from '@angular/cdk/layout';
import { MatIcon } from '@angular/material/icon';
import { ActivatedRoute, Router } from '@angular/router';
import { PaymentService } from '../../../services/payment-portal.service';
import { PaymentNavigationService } from '../../../services/payment-navigation.service';
import { APP_CONFIG, AppConfig } from '../../../../app.config';
import { IPayload, UIResponse } from '../../../services/base/policy-details';
import { CryptoService } from '../../../services/crypto.service';
import { ToastService } from '../../../services/toast.service';
import { RECAPTCHA_SETTINGS, RecaptchaModule, RecaptchaFormsModule, RecaptchaSettings } from 'ng-recaptcha';
import { ModalService } from '../../../services/modal.service';
import { map } from 'rxjs';

function resolveCaptchaSiteKey(config: AppConfig) {
    return {
        siteKey: config.recaptchaSiteKey
    } as RecaptchaSettings;
}

@Component({
    selector: 'app-payment-portal-component',
    imports: [
        RoutePageComponent,
        CommonModule,
        ReactiveFormsModule,
        MatChipsModule,
        MatIcon,
        RecaptchaModule,
        RecaptchaFormsModule,
        ButtonComponent,
        InputTextComponent
    ],
    templateUrl: './payment-portal.component.html',
    styleUrl: './payment-portal.component.scss',
    providers: [
        FieldControlService,
        {
            provide: RECAPTCHA_SETTINGS,
            useFactory: resolveCaptchaSiteKey, deps: [APP_CONFIG]
        },
    ]
})
export class PaymentPortalComponent implements OnInit {
    page: IPage;
    payload: IPayload = {} as IPayload;
    globalConstants = globalConstants;
    form: FormGroup;
    reactiveForm: FormGroup;
    showErrorMsg = signal(false);
    selectedChoice = signal('');
    isMobile = toSignal(
        this.breakpointObserver.observe(['(max-width: 768px)']).pipe(map(r => r.matches)),
        { initialValue: this.breakpointObserver.isMatched('(max-width: 768px)') }
    );

    pills = [
        { type: 'Policy', selected: false },
        { type: 'Invoice', selected: false },
        { type: 'Account', selected: false },
    ];
    referenceCategoryMap: Record<string, string> = {
        Policy: 'PolicyNumber',
        Invoice: 'InvoiceNumber',
        Account: 'AccountNumber'
    };
    referenceNumberField: IField = new Field({
        name: 'referenceNumber',
        readonly: false,
        type: 'text',
        required: true,
        validationMessages: []
    });
    zipCodeField: IField = new Field({
        name: 'zipCode',
        readonly: false,
        type: 'text',
        caption: 'Mailing zip code',
        placeholder: 'Mailing zip code',
        required: true,
        maxLength: 5,
        validationMessages: [
            {
                type: 'apiError',
                message: 'Please review the zip code number',
            },
        ],
    });

    constructor(
        private router: Router,
        private route: ActivatedRoute,
        private crypto: CryptoService,
        private toastService: ToastService,
        private modalService: ModalService,
        private paymentService: PaymentService,
        private paymentNavigationService: PaymentNavigationService,
        private fieldService: FieldControlService,
        private breakpointObserver: BreakpointObserver,
        @Inject(APP_CONFIG) private config: AppConfig
    ) {
        // First check URL query params (e.g. paymentportal?Category=PolicyNumber&search=1000021889&Zipcode=92007)
        this.route.queryParams.subscribe((query) => {
            if (query?.['Category']) {
                const selectedType = query['Category'].replace('Number', '');
                const pill = this.pills.find(p => p.type === selectedType);
                if (pill) {
                    this.onSelectChoice(pill);
                    this.referenceNumberField.value = query?.['search'] || '';
                    this.zipCodeField.value = query?.['Zipcode'] || '';

                    // Sync URL params into the navigation service for downstream pages
                    this.paymentNavigationService.setNavigationState({
                        category: query['Category'],
                        search: query['search'] || '',
                        zipcode: query['Zipcode'] || ''
                    });

                    // If all three query params are present, redirect straight to make-payment
                    if (query['Category'] && query['search'] && query['Zipcode']) {
                        this.router.navigate(['/make-payment'], {
                            queryParams: {
                                Category: query['Category'],
                                search: query['search'],
                                Zipcode: query['Zipcode']
                            }
                        });
                    }
                }
                return;
            }

            // Fallback: restore from navigation service (back-navigation from payment-center)
            const navState = this.paymentNavigationService.navigationState();
            if (navState?.category) {
                const selectedType = navState.category.replace('Number', '');
                const pill = this.pills.find(p => p.type === selectedType);
                if (pill) {
                    this.onSelectChoice(pill);
                    this.referenceNumberField.value = navState.search || '';
                    this.zipCodeField.value = navState.zipcode || '';
                }
            }
        });
    }

    ngOnInit(): void {
        this.form = this.fieldService.toFormGroup([
            this.referenceNumberField,
            this.zipCodeField,
        ]);
        this.reactiveForm = new FormGroup({
            recaptchaReactive: new FormControl(null, Validators.required),
        });
    }

    onSelectChoice(pill: any, resetForm?: boolean): void {
        this.pills.forEach(p => p.selected = false);
        pill.selected = true;
        this.selectedChoice.set(pill.type);

        if (pill.type === 'Account') {
            this.referenceNumberField.helpIcon = true;
            this.referenceNumberField.helpText = '<b>Account Number</b> Example Pattern: <br><ul><li>NNNNNNNNNNNNNNNN an example would be: 0000000012345678</li><li>NNNNNNNNNNN an example would be: 000001234567</li></ul>';
        }
        if (pill.type === 'Policy') {
            this.referenceNumberField.helpIcon = true;
            this.referenceNumberField.helpText = '<b>Policy Number</b> can be found on your Declarations page or Application <br>Policy Number Example Pattern: <ul><li>AAA NNNNNNN or AAA NNNNNNN-NN an example would be: ENG 1234567-00</li><li>AA-ANNNNNNNNNNNN or AA-ANNNNNNNNNNNNN-NN an example would be: EN-1234567891234-01</li><li>NNNNNNNNNN an example would be: 1012345678</li></ul>';
        }
        if (pill.type === 'Invoice') {
            this.referenceNumberField.helpIcon = false;
        }
        this.referenceNumberField.caption = `${pill.type} number`;
        this.referenceNumberField.placeholder = `${pill.type} number`;
        this.referenceNumberField.validationMessages = [
            {
                type: 'apiError',
                message: `Please review the ${pill.type} number`,
            },
        ];
        if (resetForm) {
            this.form.reset();
        }
    }

    resolved(captchaResponse: any) {
        this.showErrorMsg.set(!this.reactiveForm.valid);
    }

    onHelpIconClick() {
        const dialog = {
            type: 'paymentScehedule',
            helpText: true,
            message: '',
            messageExt: "You can identify your Policy either by inputting a Policy number, Invoice number or an Account number. <br>Please fill all mandatory fields before clicking on 'Continue' button. Click on 'Continue' button to proceed to payment",
            confirm: 'Ok, got it.',
            cancel: '',
            fields: [],
        };
        this.modalService.confirm(dialog);
    }

    retrievePolicyDetail(): void {
        if (this.form.valid && this.reactiveForm.valid) {
            let paymentDetailsEncryptPath = this.config.paymentDetailsEncryptPath || '';
            paymentDetailsEncryptPath = paymentDetailsEncryptPath.replace('{category}', (this.selectedChoice() || '') + 'Number');
            paymentDetailsEncryptPath = paymentDetailsEncryptPath.replace('{reference}', this.form.controls['referenceNumber'].value || '');
            paymentDetailsEncryptPath = paymentDetailsEncryptPath.replace('{zip}', this.form.controls['zipCode'].value || '');
            this.payload.encryptedData = encodeURIComponent(
                this.crypto.encryptData(paymentDetailsEncryptPath, this.config.encryptKeyGPP)
            );

            this.paymentService.callPaymentService(this.payload, this.globalConstants.policyRetrievalService).subscribe((res: UIResponse) => {
                if (res.statusCode === 200 && res.payload) {
                    if (res.payload?.retrievePolicyResponse?.statusCode === 200) {
                        this.paymentNavigationService.setNavigationState({
                            category: this.referenceCategoryMap[this.selectedChoice()],
                            search: this.form.value.referenceNumber,
                            zipcode: this.form.value.zipCode
                        });
                        this.router.navigate(['/payment-center']);
                    } else {
                        this.form.controls['referenceNumber'].setErrors({ apiError: true });
                        this.form.controls['zipCode'].setErrors({ apiError: true });
                        this.toastService.add({
                            type: 'error',
                            title: 'Error',
                            message: 'The information provided did not match our records. Please review it and try again',
                        });
                    }
                }
            });
        }
    }

    login(): void {
        window.open(this.config.psicLoginUrl, '_blank');
    }
}
